<style scoped>
.search-container {
	position: relative;
	max-width: 600px;
}

.search-input {
	border-radius: 24px;
	padding: 0.5rem 1rem 0.5rem 2.5rem;
	font-size: 0.95rem;
	border: 2px solid #dee2e6;
	transition: border-color 0.2s, box-shadow 0.2s;
	width: 100%;
}

.search-input:focus {
	border-color: #0d6efd;
	box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
	outline: none;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #6c757d;
	z-index: 5;
	pointer-events: none;
}

.search-clear {
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #6c757d;
	border: none;
	background: none;
	cursor: pointer;
	z-index: 5;
	padding: 2px 6px;
	border-radius: 50%;
}

.search-clear:hover {
	background-color: #e9ecef;
	color: #333;
}

.search-dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
	margin-top: 4px;
	max-height: 400px;
	overflow-y: auto;
	z-index: 1000;
}

.search-result-item {
	padding: 0.625rem 1rem;
	cursor: pointer;
	transition: background-color 0.15s;
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
}

.search-result-item:first-child {
	border-radius: 12px 12px 0 0;
}

.search-result-item:last-child {
	border-radius: 0 0 12px 12px;
}

.search-result-item:hover {
	background-color: #f0f4ff;
}

.search-result-item.selected {
	background-color: #e8f0fe;
}

.result-icon {
	font-size: 1.1rem;
	margin-top: 2px;
	flex-shrink: 0;
}

.result-text {
	flex: 1;
	min-width: 0;
}

.result-display-text {
	font-weight: 500;
	font-size: 0.9rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.result-subtitle {
	font-size: 0.78rem;
	color: #6c757d;
}

.result-badge {
	font-size: 0.7rem;
	padding: 1px 6px;
	border-radius: 8px;
	font-weight: 500;
	flex-shrink: 0;
	margin-top: 2px;
}

.badge-existing {
	background-color: #d1e7dd;
	color: #0f5132;
}

.badge-new {
	background-color: #f8f9fa;
	color: #6c757d;
	border: 1px solid #dee2e6;
}

.search-loading {
	padding: 0.75rem 1rem;
	text-align: center;
	color: #6c757d;
	font-size: 0.85rem;
}

.section-header {
	padding: 0.375rem 1rem;
	font-size: 0.72rem;
	font-weight: 600;
	color: #6c757d;
	background-color: #f8f9fa;
	border-bottom: 1px solid #e9ecef;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.empty-results {
	padding: 0.75rem 1rem;
	text-align: center;
	color: #6c757d;
	font-size: 0.85rem;
}
</style>

<template>
	<div class="search-container">
		<i class="bi bi-search search-icon"></i>
		<input
			ref="inputRef"
			class="form-control search-input"
			:placeholder="placeholder"
			type="text"
			v-model="searchText"
			@input="onInput"
			@keydown.escape="hideDropdown"
			@keydown.down.prevent="highlightNext"
			@keydown.up.prevent="highlightPrev"
			@keydown.enter.prevent="selectHighlighted"
			@blur="onBlur"
			@focus="onFocus"
		/>
		<button
			v-if="searchText.length > 0"
			class="search-clear"
			@click="clearSearch"
			type="button"
		>
			<i class="bi bi-x-lg"></i>
		</button>

		<!-- Autocomplete dropdown -->
		<div v-if="showDropdown" class="search-dropdown">
			<div v-if="loading" class="search-loading">
				<div class="spinner-border spinner-border-sm me-2" role="status"></div>
				Searching...
			</div>

			<div v-else-if="filteredResults.length === 0 && searchText.length >= minChars" class="empty-results">
				<i class="bi bi-search me-1"></i> No results found
			</div>

			<template v-else>
				<!-- Address results -->
				<div v-if="addressResults.length > 0">
					<div class="section-header">
						<i class="bi bi-geo-alt me-1"></i> Addresses
					</div>
					<div
						v-for="(result, index) in addressResults"
						:key="'addr-' + index"
						class="search-result-item"
						:class="{ selected: highlightIndex === indexOf(result) }"
						@mouseenter="highlightIndex = indexOf(result)"
						@mousedown.prevent="selectResult(result)"
					>
						<i class="bi bi-geo-alt result-icon" :class="result.has_communication ? 'text-success' : 'text-muted'"></i>
						<div class="result-text">
							<div class="result-display-text">{{ result.display_text }}</div>
							<div class="result-subtitle" v-if="result.subtitle">{{ result.subtitle }}</div>
						</div>
						<span class="result-badge" :class="result.has_communication ? 'badge-existing' : 'badge-new'">
							{{ result.has_communication ? 'Reported' : 'New' }}
						</span>
					</div>
				</div>

				<!-- Name results -->
				<div v-if="nameResults.length > 0">
					<div class="section-header">
						<i class="bi bi-person me-1"></i> Reporters
					</div>
					<div
						v-for="(result, index) in nameResults"
						:key="'name-' + index"
						class="search-result-item"
						:class="{ selected: highlightIndex === indexOf(result) }"
						@mouseenter="highlightIndex = indexOf(result)"
						@mousedown.prevent="selectResult(result)"
					>
						<i class="bi bi-person result-icon text-primary"></i>
						<div class="result-text">
							<div class="result-display-text">{{ result.display_text }}</div>
							<div class="result-subtitle" v-if="result.public_id">Report {{ result.public_id }}</div>
						</div>
					</div>
				</div>

				<!-- Report ID results -->
				<div v-if="idResults.length > 0">
					<div class="section-header">
						<i class="bi bi-hash me-1"></i> Report IDs
					</div>
					<div
						v-for="(result, index) in idResults"
						:key="'id-' + index"
						class="search-result-item"
						:class="{ selected: highlightIndex === indexOf(result) }"
						@mouseenter="highlightIndex = indexOf(result)"
						@mousedown.prevent="selectResult(result)"
					>
						<i class="bi bi-hash result-icon text-secondary"></i>
						<div class="result-text">
							<div class="result-display-text">{{ result.display_text }}</div>
							<div class="result-subtitle" v-if="result.subtitle">{{ result.subtitle }}</div>
						</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";

import { apiClient } from "@/client";
import type { CommunicationSearchResult, Location } from "@/type/api";

interface Emits {
	(e: "select", result: CommunicationSearchResult): void;
	(e: "clear"): void;
	(e: "search", query: string): void;
}

interface Props {
	placeholder?: string;
	minChars?: number;
}

const props = withDefaults(defineProps<Props>(), {
	placeholder: "Search by address, reporter name, or report ID...",
	minChars: 2,
});

const emit = defineEmits<Emits>();

const searchText = ref("");
const results = ref<CommunicationSearchResult[]>([]);
const loading = ref(false);
const showDropdown = ref(false);
const highlightIndex = ref(-1);
const inputRef = ref<HTMLInputElement | null>(null);

// Debounced search
const debouncedSearch = useDebounceFn(async (query: string) => {
	if (query.length < props.minChars) {
		results.value = [];
		loading.value = false;
		return;
	}

	loading.value = true;
	try {
		const data = await apiClient.JSONGet<CommunicationSearchResult[]>(
			`/api/communication/search?query=${encodeURIComponent(query)}`,
		);
		results.value = data ?? [];
	} catch (e) {
		console.error("Search failed:", e);
		results.value = [];
	} finally {
		loading.value = false;
	}
}, 300);

function onInput() {
	highlightIndex.value = -1;
	showDropdown.value = true;
	emit("search", searchText.value);
	debouncedSearch(searchText.value);
}

function onFocus() {
	if (results.value.length > 0 || loading.value) {
		showDropdown.value = true;
	}
}

function onBlur() {
	// Delay hiding the dropdown so clicks on items register
	setTimeout(() => {
		showDropdown.value = false;
		highlightIndex.value = -1;
	}, 200);
}

function hideDropdown() {
	showDropdown.value = false;
	highlightIndex.value = -1;
}

function clearSearch() {
	searchText.value = "";
	results.value = [];
	showDropdown.value = false;
	emit("clear");
	emit("search", "");
	inputRef.value?.focus();
}

function indexOf(result: CommunicationSearchResult): number {
	return results.value.indexOf(result);
}

function highlightNext() {
	if (results.value.length === 0) return;
	highlightIndex.value = Math.min(highlightIndex.value + 1, results.value.length - 1);
}

function highlightPrev() {
	if (results.value.length === 0) return;
	highlightIndex.value = Math.max(highlightIndex.value - 1, 0);
}

function selectHighlighted() {
	if (highlightIndex.value >= 0 && highlightIndex.value < results.value.length) {
		selectResult(results.value[highlightIndex.value]);
	}
}

function selectResult(result: CommunicationSearchResult) {
	searchText.value = result.display_text;
	showDropdown.value = false;
	emit("select", result);
}

// Categorized results
const addressResults = computed(() =>
	results.value.filter((r) => r.type === "address"),
);
const nameResults = computed(() =>
	results.value.filter((r) => r.type === "name"),
);
const idResults = computed(() =>
	results.value.filter((r) => r.type === "report_id"),
);

const filteredResults = computed(() => results.value);
</script>
